import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Flask, Plus, Trash, Trophy, TrendUp, TrendDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ABTesting() {
  var [tests, setTests] = useState([]);
  var [selectedTest, setSelectedTest] = useState(null);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [form, setForm] = useState({ name: '', offer_id: '', confidence_threshold: 95, variants: [{ name: 'Control', url: '', traffic_pct: 50 }, { name: 'Variant B', url: '', traffic_pct: 50 }] });

  var fetchData = function () {
    api.get('/abtesting/tests').then(function (r) { setTests(r.data); }).catch(console.error).finally(function () { setLoading(false); });
  };
  useEffect(function () { fetchData(); }, []);

  var viewTest = async function (id) {
    try { var r = await api.get('/abtesting/tests/' + id); setSelectedTest(r.data); } catch (_e) { toast.error('Failed to load test'); }
  };

  var addVariant = function () {
    setForm(function (p) {
      var vs = p.variants.concat([{ name: 'Variant ' + String.fromCharCode(66 + p.variants.length - 1), url: '', traffic_pct: 50 }]);
      return Object.assign({}, p, { variants: vs });
    });
  };

  var updateVariant = function (idx, field, val) {
    setForm(function (p) {
      var vs = p.variants.map(function (v, i) { if (i === idx) { var upd = {}; upd[field] = val; return Object.assign({}, v, upd); } return v; });
      return Object.assign({}, p, { variants: vs });
    });
  };

  var createTest = async function (e) {
    e.preventDefault();
    try {
      await api.post('/abtesting/tests', form);
      toast.success('A/B test created');
      setDialogOpen(false);
      setForm({ name: '', offer_id: '', confidence_threshold: 95, variants: [{ name: 'Control', url: '', traffic_pct: 50 }, { name: 'Variant B', url: '', traffic_pct: 50 }] });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  var deleteTest = async function (id) {
    try { await api.delete('/abtesting/tests/' + id); toast.success('Deleted'); setSelectedTest(null); fetchData(); } catch (_e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="ab-testing">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Flask size={32} className="text-purple-400" weight="duotone" /> A/B Testing
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Multivariate testing with statistical significance analysis</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="create-test-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus size={16} /> New Test</Button></DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-white">Create A/B Test</DialogTitle></DialogHeader>
            <form onSubmit={createTest} className="space-y-4">
              <div><Label className="text-zinc-300">Test Name</Label><Input data-testid="test-name" value={form.name} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { name: e.target.value }); }); }} required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div><Label className="text-zinc-300">Offer ID (optional)</Label><Input data-testid="test-offer" value={form.offer_id} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { offer_id: e.target.value }); }); }} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div><Label className="text-zinc-300">Confidence Threshold (%)</Label><Input data-testid="test-confidence" type="number" min="80" max="99" value={form.confidence_threshold} onChange={function (e) { setForm(function (p) { return Object.assign({}, p, { confidence_threshold: Number(e.target.value) }); }); }} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div>
                <div className="flex items-center justify-between mb-2"><Label className="text-zinc-300">Variants</Label><Button type="button" onClick={addVariant} size="sm" className="text-xs bg-zinc-800 text-zinc-300 gap-1"><Plus size={12} /> Add</Button></div>
                <div className="space-y-3">
                  {form.variants.map(function (v, i) {
                    return (
                      <div key={i} className="p-3 bg-zinc-950 rounded-lg border border-zinc-800 space-y-2">
                        <div className="flex gap-2">
                          <Input placeholder="Name" value={v.name} onChange={function (e) { updateVariant(i, 'name', e.target.value); }} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
                          <Input type="number" placeholder="Traffic %" value={v.traffic_pct} onChange={function (e) { updateVariant(i, 'traffic_pct', Number(e.target.value)); }} className="w-24 bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
                        </div>
                        <Input placeholder="Landing page URL" value={v.url} onChange={function (e) { updateVariant(i, 'url', e.target.value); }} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button data-testid="submit-test" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Create Test</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Tests</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">ID</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Variants</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Winner</TableHead>
              <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {tests.map(function (t, i) {
                return (
                  <TableRow key={i} className="border-zinc-800/50 cursor-pointer hover:bg-zinc-800/30" data-testid={'test-row-' + i} onClick={function () { viewTest(t.test_id); }}>
                    <TableCell className="text-zinc-500 font-mono text-xs">{t.test_id}</TableCell>
                    <TableCell className="text-zinc-200 font-medium">{t.name}</TableCell>
                    <TableCell className="text-zinc-300">{(t.variants || []).length}</TableCell>
                    <TableCell><Badge className={t.status === 'running' ? 'bg-blue-500/10 text-blue-400 text-[10px]' : t.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 text-[10px]' : 'bg-zinc-700/30 text-zinc-400 text-[10px]'}>{t.status}</Badge></TableCell>
                    <TableCell>{t.winner ? <Badge className="bg-amber-500/10 text-amber-400 text-[10px]">{t.winner}</Badge> : <span className="text-zinc-500 text-xs">-</span>}</TableCell>
                    <TableCell><Button variant="ghost" size="sm" onClick={function (e) { e.stopPropagation(); deleteTest(t.test_id); }} className="text-zinc-400 hover:text-red-400"><Trash size={14} /></Button></TableCell>
                  </TableRow>
                );
              })}
              {tests.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No A/B tests created yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedTest && (
        <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Flask size={18} className="text-purple-400" /> {selectedTest.name}</CardTitle>
              <div className="flex items-center gap-2">
                <Badge className={selectedTest.status === 'running' ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'}>{selectedTest.status}</Badge>
                <Button variant="ghost" size="sm" onClick={function () { setSelectedTest(null); }} className="text-zinc-400">Close</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {(selectedTest.variants || []).map(function (v, i) {
                var isWinner = selectedTest.winner === v.variant_id;
                var isControl = i === 0;
                return (
                  <div key={v.variant_id} className={'p-4 rounded-lg border ' + (isWinner ? 'bg-amber-500/5 border-amber-500/30' : 'bg-zinc-950 border-zinc-800')} data-testid={'variant-detail-' + i}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-semibold text-white">{v.name}</span>
                        {isControl && <Badge className="bg-zinc-700/30 text-zinc-400 text-[9px]">Control</Badge>}
                        {isWinner && <Badge className="bg-amber-500/10 text-amber-400 text-[10px] gap-1"><Trophy size={10} /> Winner</Badge>}
                      </div>
                      <span className="text-xs text-zinc-500">{v.traffic_pct}% traffic</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
                      <div><p className="text-[10px] text-zinc-500 uppercase">Impressions</p><p className="text-lg font-bold font-heading text-blue-400">{(v.impressions || 0).toLocaleString()}</p></div>
                      <div><p className="text-[10px] text-zinc-500 uppercase">Conversions</p><p className="text-lg font-bold font-heading text-emerald-400">{v.conversions || 0}</p></div>
                      <div><p className="text-[10px] text-zinc-500 uppercase">Conv Rate</p><p className="text-lg font-bold font-heading text-white">{v.conversion_rate || 0}%</p></div>
                      <div><p className="text-[10px] text-zinc-500 uppercase">Lift</p><p className={'text-lg font-bold font-heading ' + ((v.lift || 0) > 0 ? 'text-emerald-400' : (v.lift || 0) < 0 ? 'text-red-400' : 'text-zinc-400')}>
                        {(v.lift || 0) > 0 ? '+' : ''}{v.lift || 0}%
                      </p></div>
                      <div><p className="text-[10px] text-zinc-500 uppercase">Significance</p>
                        {v.p_value !== undefined ? (
                          <p className={'text-lg font-bold font-heading ' + (v.significant ? 'text-emerald-400' : 'text-zinc-400')}>
                            {v.significant ? 'Yes' : 'No'} <span className="text-xs text-zinc-500">p={v.p_value}</span>
                          </p>
                        ) : <p className="text-lg font-bold font-heading text-zinc-500">-</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-zinc-500 mt-3">Confidence threshold: {selectedTest.confidence_threshold}%. Winner auto-declared when significance reached with 100+ impressions per variant.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
